import React, { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';

// Browser-native dictation (Web Speech API). Chrome/Edge support it; on
// unsupported browsers the button simply doesn't render.
const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

// Reusable mic button. Calls onTranscript(text) with each finalized chunk of
// speech, so the caller decides how to append it (e.g. into a textarea).
// `stopWhen` is an optional counter a caller bumps to switch dictation off from outside -
// send a message, stop listening. It starts falsy so mounting does not trigger it.
export default function MicButton({
  onTranscript, title = 'Dictate', size = 16, style = {}, stopWhen = 0,
}) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  // ⚠️ THE ONLY RELIABLE RECORD OF WHETHER THE USER WANTS TO BE DICTATING.
  // `listening` is state, so the recognizer's own onend callback closes over a
  // stale copy of it and cannot be used to decide whether to restart.
  const wantRef = useRef(false);

  // Stop the recognizer if the component unmounts mid-dictation.
  useEffect(() => () => { wantRef.current = false; try { recRef.current?.stop(); } catch {} }, []);

  // ⚠️ WITHOUT THIS, SENDING LEAVES THE MICROPHONE ON. The recognizer is `continuous`, so
  // it keeps listening after the message goes, and the next thing said in the room lands
  // in the empty box. Bumping stopWhen on send switches it off.
  useEffect(() => {
    if (!stopWhen) return;
    wantRef.current = false;
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  }, [stopWhen]);

  if (!SpeechRecognition) return null;

  // Builds a configured recognizer. Separate from start() because onend has to
  // be able to construct a fresh one - a SpeechRecognition instance cannot be
  // restarted once the browser has ended it.
  const build = () => {
    const rec = new SpeechRecognition();
    rec.continuous = true;        // necessary, but not sufficient - see onend
    rec.interimResults = false;   // only fire on finalized phrases
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      let finalText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
      }
      if (finalText.trim()) onTranscript(finalText.trim());
    };
    // ⚠️ `continuous = true` DOES NOT KEEP CHROME LISTENING. Chrome ends the
    // session on its own after a stretch of silence - a few seconds is enough -
    // and fires onend regardless of this flag. That is the whole reason
    // dictation appeared to "time itself out" mid-thought: Greg, 2026-09-21,
    // "there's nothing worse than talking, talking, talking, and looking over
    // and seeing that the thing is timed itself out."
    //
    // There is no timeout setting to raise. The fix is to start a new session
    // whenever Chrome ends one the user did not end, which is what wantRef
    // distinguishes. To the person talking it is simply continuous.
    rec.onend = () => {
      recRef.current = null;
      if (!wantRef.current) { setListening(false); return; }
      try {
        const next = build();
        recRef.current = next;
        next.start();
      } catch {
        // start() throws if the engine is still tearing the old session down.
        // One retry on the next tick covers it; giving up here is what would
        // put us back to a mic that stops on its own.
        setTimeout(() => {
          if (!wantRef.current) return;
          try { const again = build(); recRef.current = again; again.start(); }
          catch { wantRef.current = false; setListening(false); }
        }, 250);
      }
    };
    // `no-speech` and `aborted` are the ordinary end of a silent stretch, not
    // failures - let onend restart them. Anything else (no permission, no
    // device) is real and must stop, or this retries forever.
    rec.onerror = (e) => {
      const code = e && e.error;
      if (code !== 'no-speech' && code !== 'aborted') {
        wantRef.current = false;
        setListening(false);
      }
      recRef.current = null;
    };
    return rec;
  };

  const start = () => {
    wantRef.current = true;
    const rec = build();
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch {}
  };

  const stop = () => {
    wantRef.current = false;
    try { recRef.current?.stop(); } catch {}
    setListening(false);
  };

  return (
    <button
      type="button"
      onClick={() => (listening ? stop() : start())}
      // ⚠️ THIS IS WHY DICTATION THEN ENTER WORKS. Greg, 2026-09-11: "I hit the mic button,
      // I talk, and I wanna be able to just slap the enter key, but I have to click back in
      // the box." Clicking a button moves focus to it, so Enter was hitting the mic and
      // toggling it again rather than sending. Preventing the default on mousedown stops
      // the button taking focus at all, so it stays in whatever field you were typing in.
      //
      // Mouse only. Tab and Enter still reach the button normally, so a keyboard user can
      // still start dictation - which is the thing a blanket tabIndex={-1} would have
      // broken.
      onMouseDown={(e) => e.preventDefault()}
      title={listening ? 'Stop dictation' : title}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: size + 16, height: size + 16, borderRadius: '50%', cursor: 'pointer',
        border: 'none', padding: 0,
        background: listening ? 'var(--orange, #DC582A)' : 'rgba(0,0,0,0.06)',
        color: listening ? '#fff' : '#6b7280',
        animation: listening ? 'cpxMicPulse 1.2s ease-in-out infinite' : 'none',
        ...style,
      }}
    >
      <Mic size={size} />
    </button>
  );
}
